/**
 * Librarian Task Management API
 * Handles librarian daily tasks including CRUD operations
 */

import apiBase, { PaginatedResponse } from './api-base';

/**
 * Task type definitions
 */
export interface LibrarianTask {
  id: number;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: 'circulation' | 'inventory' | 'admin' | 'collection' | 'book_management' | 'maintenance';
  completed: boolean;
  due_time: string | null;
  estimated_duration: string;
  assigned_by: string | null;
  book_management_type?: 'issue' | 'return' | 'add' | 'maintenance' | 'audit';
  auto_generated: boolean;
  related_books?: Array<{
    id: number;
    title: string;
    count?: number;
  }>;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface CreateTaskData {
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: 'circulation' | 'inventory' | 'admin' | 'collection' | 'book_management' | 'maintenance';
  due_time?: string;
  estimated_duration?: string;
  book_management_type?: 'issue' | 'return' | 'add' | 'maintenance' | 'audit';
  related_books?: Array<{
    id: number;
    title: string;
    count?: number;
  }>;
}

export interface UpdateTaskData {
  title?: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  category?: 'circulation' | 'inventory' | 'admin' | 'collection' | 'book_management' | 'maintenance';
  completed?: boolean;
  /** A time of day ("HH:MM"); null clears it. */
  due_time?: string | null;
  estimated_duration?: string;
  /** '' clears it. */
  book_management_type?: 'issue' | 'return' | 'add' | 'maintenance' | 'audit' | '';
}

export interface TaskQueryParams {
  completed?: boolean;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  category?: 'circulation' | 'inventory' | 'admin' | 'collection' | 'book_management' | 'maintenance';
  auto_generated?: boolean;
}

/**
 * Librarian Task Management API endpoints
 */
export const librarianTaskApi = {
  /**
   * Get list of tasks
   * @param params - Query parameters for filtering
   * @returns Promise with task list
   */
  getTasks: async (params?: TaskQueryParams): Promise<{ count: number; results: LibrarianTask[] }> => {
    const response = await apiBase.get('/librarian/tasks/', { params });
    return response.data;
  },

  /**
   * Get a specific task by ID
   * @param taskId - Task ID
   * @returns Promise with task data
   */
  getTask: async (taskId: number): Promise<LibrarianTask> => {
    const response = await apiBase.get(`/librarian/tasks/${taskId}/`);
    return response.data;
  },

  /**
   * Create a new task
   * @param data - Task creation data
   * @returns Promise with created task
   */
  createTask: async (data: CreateTaskData): Promise<LibrarianTask> => {
    const response = await apiBase.post('/librarian/tasks/', data);
    return response.data;
  },

  /**
   * Update an existing task
   * @param taskId - Task ID
   * @param data - Task update data
   * @returns Promise with updated task
   */
  updateTask: async (taskId: number, data: UpdateTaskData): Promise<LibrarianTask> => {
    const response = await apiBase.put(`/librarian/tasks/${taskId}/`, data);
    return response.data;
  },

  /**
   * Delete a task
   * @param taskId - Task ID
   * @returns Promise with deletion result
   */
  deleteTask: async (taskId: number): Promise<{ message: string }> => {
    const response = await apiBase.delete(`/librarian/tasks/${taskId}/`);
    return response.data;
  },

  /**
   * Mark a task as completed
   * @param taskId - Task ID
   * @returns Promise with updated task
   */
  completeTask: async (taskId: number): Promise<LibrarianTask> => {
    const response = await apiBase.post(`/librarian/tasks/${taskId}/complete/`);
    return response.data;
  },
};
